/**
 * EVM transaction 由来 action の保存と fiscal lock 退避を担当する。
 */
import type { Action } from '@0xtorch/core'
import {
  blobToHex,
  divideArrayIntoChunks,
  hexToBlob,
  tokenTransferCountVersion,
} from '@pkg/basic'
import {
  type LowerHex,
  type TransactionDecoded,
  createEvmActionSource,
  createEvmAddressId,
} from '@pkg/evm'
import { and, eq, inArray, sql } from 'drizzle-orm'
import type { SQLiteInsertValue } from 'drizzle-orm/sqlite-core'
import {
  actionSourceColumnCount,
  cryptoCurrencyColumnCount,
  nftColumnCount,
  sqliteMaxHostParameterCount,
} from '../constants'
import {
  accountEvmTxIndexRelationTable,
  accountTable,
  actionSourceTable,
  actionTable,
  cryptoCurrencyTable,
  type erc20TokenTable,
  lockedPeriodActionTable,
  nftTable,
  portfolioTable,
} from '../schema'
import type { DatabaseWithTransaction } from '../types'
import { conflictUpdateAllExcept, getMaxInsertRowCount } from '../utils'
import { applyActionEditRulesToInsertedActionsOnDatabase } from './write/applyActionEditRulesToInsertedActions'
import { deleteAssistantProposalsForSourceIds } from './write/deleteAssistantProposalsForActionKeys'
import {
  applyRulesToLockedInsertRowsInTransaction,
  createLockedActionInsertQueries,
  createOpenActionInsertQueries,
  filterActionAccountRelations,
  getClosedAccountingPeriodBoundary,
  partitionSourceIdsByFiscalLock,
  prepareFiscalLockInsertRows,
} from './write/fiscalLock'

type SaveEvmTxAndActionsParameters = {
  readonly database: DatabaseWithTransaction
  readonly chainId: number
  readonly transactions: readonly TransactionDecoded[]
  readonly actions: readonly Action[]
  readonly tokenTransferCounts?: ReadonlyMap<LowerHex, number>
}

/**
 * EVM解析結果を保存し、現在の計算開始日時より前の取引を除外する。
 * @param params DB、チェーン、解析済み取引、生成Action、トークン移動件数。
 * @returns 保存と解析状態更新が完了した時点でresolveする。
 */
export const saveEvmTxAndActions = async ({
  database: { database, transaction },
  chainId,
  transactions: fetchedTransactions,
  actions: generatedActions,
  tokenTransferCounts,
}: SaveEvmTxAndActionsParameters) => {
  const portfolioRows = await database
    .select({ startTime: portfolioTable.startTime })
    .from(portfolioTable)
    .limit(1)
  const startTime = portfolioRows[0]?.startTime?.getTime() ?? 0
  const transactions = fetchedTransactions.filter(
    ({ timestamp }) => timestamp >= startTime,
  )
  const excludedSources = new Set<string>(
    fetchedTransactions
      .filter(({ timestamp }) => timestamp < startTime)
      .map(({ hash }) =>
        createEvmActionSource({ chainId, transactionHash: hash }),
      ),
  )
  const actions = generatedActions.filter(
    ({ timestamp, source }) =>
      timestamp >= startTime && !excludedSources.has(source),
  )
  // 同期中に開始日が変更された場合も、除外取引を再解析し続けない。
  const hashes = new Set(fetchedTransactions.map((tx) => tx.hash))
  const accountRows = await database
    .select({ id: accountTable.id, address: accountTable.evmAddress })
    .from(accountTable)
    .where(eq(accountTable.evmChainId, chainId))
  const accounts: { id: number; address: LowerHex }[] = []
  for (const row of accountRows) {
    if (row.address === null) {
      continue
    }
    accounts.push({ id: row.id, address: blobToHex(row.address) })
  }

  const { erc20Tokens } = generateErc20Data({
    chainId,
    transactions,
    accounts,
  })
  const actionAccountRelations = generateActionAccountRelations({
    chainId,
    transactions,
    actions,
    accounts,
  })
  const closedBoundary = await getClosedAccountingPeriodBoundary({
    database: { database, transaction },
  })
  const { openSourceIds, lockedSourceIds, unclassifiedSourceIds } =
    partitionSourceIdsByFiscalLock({
      sourceRows: transactions.map((tx) => ({
        sourceId: createEvmActionSource({
          chainId,
          transactionHash: tx.hash,
        }),
        timestamp: tx.timestamp,
      })),
      closedBoundary,
    })
  const deletingOpenSourceIds = [
    ...new Set([...openSourceIds, ...unclassifiedSourceIds]),
  ]
  const deletingLockedSourceIds = [
    ...new Set([...lockedSourceIds, ...unclassifiedSourceIds]),
  ]

  const {
    openActionIds: insertedActionIdList,
    openInsertRows,
    lockedInsertRows,
  } = await prepareFiscalLockInsertRows({
    database: { database, transaction },
    actions,
    createLockedActionAccountRelations: ({ lockedActions }) =>
      filterActionAccountRelations({
        actionAccountRelations,
        actionIds: lockedActions.map(
          (action) => `${action.source}_${action.order}`,
        ),
      }),
  })
  const shouldInvalidateCalculation =
    (fetchedTransactions.length === 0 && generatedActions.length === 0) ||
    deletingOpenSourceIds.length > 0 ||
    insertedActionIdList.length > 0

  // SQLite でCurrencyIdが存在しない場合があるため処理を追加
  // workers/evm-token/src/interface/v1/erc20.ts あたりで処理失敗した場合でもDBに保存されるようにしてもよいはず
  const erc20CurrencyIds = [
    ...new Set(
      erc20Tokens
        .map((token) => token.currencyId)
        .filter((id): id is string => id !== undefined),
    ),
  ]
  const existingCryptoIds = new Set(
    [...openInsertRows.cryptoRows, ...lockedInsertRows.cryptoRows].map(
      (crypto) => crypto.id,
    ),
  )
  const missingCurrencyIds = erc20CurrencyIds.filter(
    (id) => !existingCryptoIds.has(id),
  )
  // cryptoSqlsを配列として再定義（読み取り専用を解除）
  const extendedCryptoSqls = [
    ...openInsertRows.cryptoRows,
    ...lockedInsertRows.cryptoRows,
  ]
  if (missingCurrencyIds.length > 0) {
    for (const currencyId of missingCurrencyIds) {
      extendedCryptoSqls.push({
        id: currencyId,
        name: `Unknown Currency ${currencyId}`,
        symbol: 'UNKNOWN',
        icon: null,
        coingeckoId: null,
        marketCapUsd: null,
        priceDatasourceId: null,
        updatedAt: Date.now(),
      })
    }
  }

  await transaction([
    (tx) => [
      deleteAssistantProposalsForSourceIds({
        database: tx,
        sourceIds: deletingOpenSourceIds,
      }),
    ],
    (tx) => [
      ...(shouldInvalidateCalculation
        ? [tx.update(portfolioTable).set({ isTxGenerated: false })]
        : []),
      // accountEvmIndexRelation.analyzed を true に更新
      ...divideArrayIntoChunks(
        [...hashes],
        sqliteMaxHostParameterCount - 2,
      ).map((chunk) =>
        tx
          .update(accountEvmTxIndexRelationTable)
          .set({ analyzed: true })
          .where(
            and(
              inArray(
                accountEvmTxIndexRelationTable.accountId,
                tx
                  .select({ id: accountTable.id })
                  .from(accountTable)
                  .where(eq(accountTable.evmChainId, chainId)),
              ),
              inArray(
                accountEvmTxIndexRelationTable.hash,
                chunk.map((hash) => hexToBlob(hash)),
              ),
            ),
          ),
      ),
      // account.syncing を analyzed=false の accountEvmIndexRelation の存在に合わせて更新
      ...accounts.map(({ id }) =>
        tx
          .update(accountTable)
          .set({
            syncing: sql`(EXISTS (SELECT 1 FROM ${accountEvmTxIndexRelationTable} WHERE ${accountEvmTxIndexRelationTable.accountId} = ${id} AND ${accountEvmTxIndexRelationTable.analyzed} = ${false}))`,
          })
          .where(eq(accountTable.id, id)),
      ),
      // decoded transaction を actionSource に保存 (upsert)
      ...divideArrayIntoChunks(
        transactions,
        getMaxInsertRowCount(actionSourceColumnCount, true),
      ).map((chunk) =>
        tx
          .insert(actionSourceTable)
          .values(
            chunk.map((tx) => ({
              id: createEvmActionSource({
                chainId: tx.chainId,
                transactionHash: tx.hash,
              }),
              type: 'evm' as const,
              evmChainId: tx.chainId,
              evmHash: hexToBlob(tx.hash),
              evmFrom: hexToBlob(tx.from.address),
              evmTo: tx.to === undefined ? null : hexToBlob(tx.to.address),
              evmStatus: tx.status,
              evmFunctionId:
                tx.input.length >= 10
                  ? hexToBlob(`0x${tx.input.slice(2, 10)}`)
                  : undefined,
              evmFunctionName: tx.function?.name,
              tokenTransferCount: tokenTransferCounts?.get(tx.hash),
              tokenTransferCountVersion:
                tokenTransferCounts?.has(tx.hash) === true
                  ? tokenTransferCountVersion.evm
                  : undefined,
            })),
          )
          .onConflictDoUpdate({
            target: [actionSourceTable.id],
            set: {
              ...conflictUpdateAllExcept(actionSourceTable, [
                'id',
                'tokenTransferCount',
                'tokenTransferCountVersion',
              ]),
              tokenTransferCount: sql`coalesce(excluded."tokenTransferCount", ${actionSourceTable.tokenTransferCount})`,
              tokenTransferCountVersion: sql`coalesce(excluded."tokenTransferCountVersion", ${actionSourceTable.tokenTransferCountVersion})`,
            },
          }),
      ),
      // sources に関連する action を削除
      ...divideArrayIntoChunks(
        deletingOpenSourceIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx.delete(actionTable).where(inArray(actionTable.sourceId, [...chunk])),
      ),
      ...divideArrayIntoChunks(
        deletingLockedSourceIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .delete(lockedPeriodActionTable)
          .where(inArray(lockedPeriodActionTable.sourceId, [...chunk])),
      ),
      // crypto
      ...divideArrayIntoChunks(
        extendedCryptoSqls,
        getMaxInsertRowCount(cryptoCurrencyColumnCount),
      ).map((chunk) =>
        tx
          .insert(cryptoCurrencyTable)
          .values([...chunk])
          .onConflictDoNothing(),
      ),
      // nft
      ...divideArrayIntoChunks(
        [...openInsertRows.nftRows, ...lockedInsertRows.nftRows],
        getMaxInsertRowCount(nftColumnCount),
      ).map((chunk) =>
        tx
          .insert(nftTable)
          .values([...chunk])
          .onConflictDoNothing(),
      ),
    ],
    (tx) => [
      (async () => {
        const preparedLockedInsertRows =
          await applyRulesToLockedInsertRowsInTransaction({
            database: tx,
            lockedInsertRows,
          })

        await Promise.all([
          ...createOpenActionInsertQueries({
            tx,
            openInsertRows,
            actionAccountRelationRows: filterActionAccountRelations({
              actionAccountRelations,
              actionIds: insertedActionIdList,
            }),
          }),
          ...createLockedActionInsertQueries({
            tx,
            lockedInsertRows: preparedLockedInsertRows,
          }),
        ])

        if (insertedActionIdList.length > 0) {
          await applyActionEditRulesToInsertedActionsOnDatabase({
            database: tx,
            actionIdList: insertedActionIdList,
          })
        }
      })(),
    ],
  ])
}

// transaction と action から actionAccountRelation data を作成
// action transfer の from, to に account address が含まれていれば account に関連づける
// transaction from, to に account address が含まれていれば account に関連づける
const generateActionAccountRelations = (params: {
  chainId: number
  transactions: readonly TransactionDecoded[]
  actions: readonly Action[]
  accounts: readonly { id: number; address: LowerHex }[]
}): readonly { actionId: string; accountId: number }[] => {
  const { chainId, transactions, actions, accounts } = params

  const addressMap = new Map<LowerHex, number>(
    accounts.map(({ address, id }) => [address, id]),
  )
  const evmAddressIdMap = new Map<string, number>(
    accounts.map(({ address, id }) => [
      createEvmAddressId({ chainId, address }),
      id,
    ]),
  )
  const sourceTransactionFromMap = new Map<string, LowerHex>(
    transactions.map((tx) => [
      createEvmActionSource({ chainId, transactionHash: tx.hash }),
      tx.from.address,
    ]),
  )

  const relationMap = new Map<string, { actionId: string; accountId: number }>()
  for (const action of actions) {
    const actionId = `${action.source}_${action.order}`
    // transaction from と一致する account との relation data 作成
    const from = sourceTransactionFromMap.get(action.source)
    const txFromAccountId =
      from === undefined ? undefined : addressMap.get(from)
    if (txFromAccountId !== undefined) {
      const id = `${actionId}/${txFromAccountId}`
      if (!relationMap.has(id)) {
        relationMap.set(actionId, { actionId, accountId: txFromAccountId })
      }
    }

    // action transfer の from, to と一致する account を追加
    for (const transfer of action.transfers) {
      const transferFromAccountId =
        transfer.from === undefined
          ? undefined
          : evmAddressIdMap.get(transfer.from)
      if (transferFromAccountId !== undefined) {
        const id = `${actionId}/${transferFromAccountId}`
        if (!relationMap.has(id)) {
          relationMap.set(actionId, {
            actionId,
            accountId: transferFromAccountId,
          })
        }
      }
      const transferToAccountId =
        transfer.to === undefined ? undefined : evmAddressIdMap.get(transfer.to)
      if (transferToAccountId !== undefined) {
        const id = `${actionId}/${transferToAccountId}`
        if (!relationMap.has(id)) {
          relationMap.set(actionId, {
            actionId,
            accountId: transferToAccountId,
          })
        }
      }
    }
  }

  return [...relationMap.values()]
}

const generateErc20Data = (params: {
  chainId: number
  transactions: readonly TransactionDecoded[]
  accounts: readonly { id: number; address: LowerHex }[]
}): {
  erc20Tokens: readonly SQLiteInsertValue<typeof erc20TokenTable>[]
} => {
  const { chainId, transactions, accounts } = params

  const addressMap = new Map<LowerHex, number>(
    accounts.map(({ address, id }) => [address, id]),
  )
  const erc20TokenSqlMap = new Map<
    string,
    SQLiteInsertValue<typeof erc20TokenTable>
  >()

  for (const transaction of transactions) {
    for (const transfer of transaction.erc20Transfers) {
      const fromAccountId = addressMap.get(transfer.from)
      const toAccountId = addressMap.get(transfer.to)
      if (fromAccountId === undefined && toAccountId === undefined) {
        continue
      }
      const tokenId = createEvmAddressId({ chainId, address: transfer.address })
      if (!erc20TokenSqlMap.has(tokenId)) {
        erc20TokenSqlMap.set(tokenId, {
          id: tokenId,
          chainId,
          address: hexToBlob(transfer.address),
          name: transfer.token.name,
          symbol: transfer.token.symbol,
          decimals: transfer.token.decimals,
          currencyId: transfer.token.currency.id ?? `unknown_${tokenId}`,
        })
      }
    }
  }

  return {
    erc20Tokens: [...erc20TokenSqlMap.values()],
  }
}
